import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="theme-switcher"
export default class extends Controller {
  static targets = ["systemButton", "lightButton", "darkButton", "terminalButton", "halloweenButton"]
  
  themes = ["system", "light", "dark", "terminal"]
  currentThemeIndex = 0
  
  connect() {
    this.loadSavedTheme()
    this.setupSystemThemeListener()
    this.checkHalloweenSeason()
  }
  
  checkHalloweenSeason() {
    const now = new Date()
    const year = now.getFullYear()
    // Halloween available October 26 - November 3
    const startDate = new Date(year, 10 - 1, 26) // Oct 26 (month 10, converted to 0-indexed)
    const endDate = new Date(year, 11 - 1, 3, 23, 59, 59) // Nov 3 EOD (month 11, converted to 0-indexed)
    
    if (now >= startDate && now <= endDate) {
      // Add halloween to available themes
      if (!this.themes.includes("halloween")) {
        this.themes.push("halloween")
      }
      // Show halloween button if it exists
      if (this.hasHalloweenButtonTarget) {
        this.halloweenButtonTarget.classList.remove("hidden")
      }
    } else {
      // Remove halloween from themes
      this.themes = this.themes.filter(t => t !== "halloween")
      // Hide halloween button
      if (this.hasHalloweenButtonTarget) {
        this.halloweenButtonTarget.classList.add("hidden")
      }
      // If user has halloween saved, switch to system
      if (this.getCurrentTheme() === "halloween") {
        this.applyThemeChange("system")
      }
    }
  }
  
  setupSystemThemeListener() {
    // Listen for system theme changes
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    this.boundHandler = (e) => this.handleSystemThemeChange(e)
    
    // Use the modern addEventListener method
    this.mediaQuery.addEventListener('change', this.boundHandler)
  }
  
  disconnect() {
    // Clean up listener when component is destroyed
    if (this.mediaQuery && this.boundHandler) {
      this.mediaQuery.removeEventListener('change', this.boundHandler)
    }
  }
  
  handleSystemThemeChange(e) {
    // Only update if we're currently in system mode
    const currentTheme = this.getCurrentTheme()
    if (currentTheme === "system") {
      this.applySystemTheme()
      this.updateButtonStates("system")
    }
  }
  
  setTheme(event) {
    const theme = event.params.theme
    this.currentThemeIndex = this.themes.indexOf(theme)
    this.applyThemeChange(theme)
  }

  toggle() {
    this.currentThemeIndex = (this.currentThemeIndex + 1) % this.themes.length
    const newTheme = this.themes[this.currentThemeIndex]
    this.applyThemeChange(newTheme)
  }
  
  loadSavedTheme() {
    const savedTheme = localStorage.getItem("theme")
    
    if (savedTheme) {
      // Apply whatever theme is saved, even if it's not in the current themes array
      // (e.g., halloween outside its season - DaisyUI will handle gracefully)
      this.currentThemeIndex = this.themes.indexOf(savedTheme)
      this.applyTheme(savedTheme)
    } else {
      // No saved theme means system mode
      this.currentThemeIndex = 0 // system is first in array
      this.applySystemTheme()
    }
    
    this.updateButtonStates(this.getCurrentTheme())
  }
  
  applyThemeChange(theme) {
    if (theme === "system") {
      localStorage.removeItem("theme")
      this.applySystemTheme()
    } else {
        localStorage.setItem("theme", theme)
        this.applyTheme(theme)
    }

    this.updateButtonStates(theme)
    
    // Dispatch event so other components can react to theme changes
    document.dispatchEvent(new CustomEvent('theme-changed'))
  }
  
  applyTheme(theme) {
    document.documentElement.setAttribute("data-theme", theme)
    this.updateMetaThemeColor(theme)
  }

  applySystemTheme() {
    // Check system preference and apply our custom theme accordingly
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const systemTheme = prefersDark ? "dark" : "light"
    document.documentElement.setAttribute("data-theme", systemTheme)
    this.updateMetaThemeColor(systemTheme)
  }
  
  updateMetaThemeColor(theme) {
    // Colors match daisyUI --color-base-100 from app/assets/stylesheets/daisyui.css
    const colors = {
      light: "oklch(100% 0 0)",      // Light theme base-100
      dark: "oklch(0.18 0.02 255)",  // Dark theme base-100
      halloween: "oklch(0.21 0.006 56.043)", // Halloween theme bg
      terminal: "#0b0b0a"
    }
    
    const metaTag = document.querySelector('meta[name="theme-color"]')
    if (metaTag) {
      metaTag.setAttribute('content', colors[theme] || colors.light)
    }
  }

  removeTheme() {
    document.documentElement.removeAttribute("data-theme")
  }
  
  getCurrentTheme() {
    const savedTheme = localStorage.getItem("theme")
    return savedTheme || "system"
  }
  
  updateButtonStates(activeTheme) {
    // Check if targets exist before manipulating them
    if (!this.hasSystemButtonTarget || !this.hasLightButtonTarget || !this.hasDarkButtonTarget || !this.hasTerminalButtonTarget) {
      return
    }

    // Reset all buttons to inactive state (gray background)
    this.systemButtonTarget.classList.remove("bg-base-100", "shadow-sm")
    this.lightButtonTarget.classList.remove("bg-base-100", "shadow-sm")
    this.darkButtonTarget.classList.remove("bg-base-100", "shadow-sm")
    this.terminalButtonTarget.classList.remove("bg-base-100", "shadow-sm")
    if (this.hasHalloweenButtonTarget) {
      this.halloweenButtonTarget.classList.remove("bg-base-100", "shadow-sm")
    }
    
    // Set active state for the current theme button (highlighted with white/lighter background)
    switch(activeTheme) {
      case "system":
        this.systemButtonTarget.classList.add("bg-base-100", "shadow-sm")
        break
      case "light":
        this.lightButtonTarget.classList.add("bg-base-100", "shadow-sm")
        break
      case "dark":
        this.darkButtonTarget.classList.add("bg-base-100", "shadow-sm")
        break
      case "terminal":
        this.terminalButtonTarget.classList.add("bg-base-100", "shadow-sm")
        break
      case "halloween":
        if (this.hasHalloweenButtonTarget) {
          this.halloweenButtonTarget.classList.add("bg-base-100", "shadow-sm")
        }
        break
    }
  }
}
